/**
 * OS-level desktop notifications via the Web Notification API.
 *
 * Scope of THIS module: foreground notifications only — fires while at least
 * one helpdesk tab is open (visible OR in another tab/app). Closing the browser
 * stops them. That's the right tradeoff vs Web Push (service worker + VAPID +
 * backend subscription store) — much smaller surface, zero infra, immediately
 * useful on every desktop browser.
 *
 * Real background push (notifications when the browser is closed, or iPhone PWA
 * push) is a separate piece of work documented at the bottom of this file.
 *
 * Browser support today (foreground, this module):
 *  - macOS Chrome/Firefox/Safari/Edge:  ✅ works, fires native macOS banner
 *  - Windows Chrome/Firefox/Edge:        ✅ works, Action Center banner
 *  - Linux Chrome/Firefox:               ✅ works, libnotify
 *  - Android Chrome:                     ✅ works
 *  - iOS Safari (regular tab):           ❌ NOT supported by Apple, ever
 *  - iOS Safari (PWA installed via Add-to-Home-Screen, iOS 16.4+):
 *                                        ⚠️  needs Web Push (not built yet)
 */

const PREF_KEY = 'os_notif_enabled';
const ONLY_HIDDEN_KEY = 'os_notif_only_hidden';

export function isOsNotifSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getOsNotifPermission() {
  if (!isOsNotifSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/**
 * Effective enabled state: permission granted AND user hasn't toggled it off.
 * Default ON once permission is granted (assume the act of granting = consent).
 */
export function isOsNotifEnabled() {
  if (!isOsNotifSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  return localStorage.getItem(PREF_KEY) !== '0';
}

export function setOsNotifEnabled(enabled) {
  localStorage.setItem(PREF_KEY, enabled ? '1' : '0');
}

/**
 * "Only fire when the tab is hidden" — default ON. Most users don't want a
 * native banner duplicating the in-app toast they already see.
 */
export function isOsNotifOnlyWhenHidden() {
  return localStorage.getItem(ONLY_HIDDEN_KEY) !== '0';
}

export function setOsNotifOnlyWhenHidden(only) {
  localStorage.setItem(ONLY_HIDDEN_KEY, only ? '1' : '0');
}

export async function requestOsNotifPermission() {
  if (!isOsNotifSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    if (result === 'granted') setOsNotifEnabled(true);
    return result;
  } catch {
    return 'denied';
  }
}

/**
 * Show a native OS notification, respecting user prefs and visibility rules.
 *
 * @param {Object} opts
 * @param {string} opts.title              required, shown bold
 * @param {string} [opts.body]             second line, plain text
 * @param {string} [opts.link]             clicking the notification navigates here (in-app)
 * @param {string} [opts.tag]              same tag = replace previous (no spam for same ticket)
 * @param {boolean} [opts.requireInteraction]  keep visible until user dismisses (default: false, auto-close 8s)
 * @returns {Notification|null}
 */
export function tryOsNotify({ title, body, link, tag, requireInteraction = false }) {
  if (!title) return null;
  if (!isOsNotifEnabled()) return null;
  // Don't fire when tab is already focused — user is looking, the toast is enough
  if (isOsNotifOnlyWhenHidden() && document.visibilityState === 'visible' && document.hasFocus()) return null;

  try {
    const n = new Notification(title, {
      body: body || '',
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: tag || undefined,
      renotify: !!tag, // re-fire (with sound) if same tag arrives again
      requireInteraction,
      silent: false,
    });
    n.onclick = () => {
      try { window.focus(); } catch {}
      if (link) {
        // Use full nav so it works even if the tab was backgrounded for hours
        try {
          const url = new URL(link, window.location.origin);
          window.location.href = url.toString();
        } catch {
          window.location.href = link;
        }
      }
      try { n.close(); } catch {}
    };
    if (!requireInteraction) {
      // Most browsers auto-close in ~5s anyway, but Linux libnotify can be sticky
      setTimeout(() => { try { n.close(); } catch {} }, 8000);
    }
    return n;
  } catch (err) {
    console.warn('[OS-NOTIF] Failed to show:', err.message);
    return null;
  }
}

/* ── Platform detection (used to show iPhone-specific guidance) ── */

export function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Mac; treat any iPad/iPhone/iPod UA as iOS
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

/* ───────────────────────────────────────────────────────────────────────
 * Web Push — fires notifications even when the browser is CLOSED, and is
 * the ONLY way to get OS notifications on iPhone (iOS 16.4+ PWA installs).
 *
 * Pairs with: backend/src/services/pushService.js + routes/push.js + sw.js
 * VAPID keys live in env on the server side.
 * ─────────────────────────────────────────────────────────────────────── */

import api from './api';

export function isWebPushSupported() {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

// Convert VAPID base64url → Uint8Array (PushManager needs raw bytes)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let _swRegistration = null;
async function getRegistration() {
  if (_swRegistration) return _swRegistration;
  if (!('serviceWorker' in navigator)) return null;
  // The SW is registered at app startup in index.js — we just retrieve it here.
  _swRegistration = await navigator.serviceWorker.ready;
  return _swRegistration;
}

/**
 * Subscribe THIS browser to web push for the current user. Idempotent — calling
 * twice with the same browser results in the same endpoint, and the backend
 * UPSERTs by endpoint.
 *
 * Returns: { ok: true } on success, { ok: false, reason } on failure.
 */
export async function subscribeWebPush() {
  if (!isWebPushSupported()) return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'permission' };

  try {
    const reg = await getRegistration();
    if (!reg) return { ok: false, reason: 'no-sw' };

    // Get the server's public VAPID key
    const { data } = await api.get('/push/vapid-public-key');
    if (!data?.publicKey) return { ok: false, reason: 'no-vapid' };

    // Reuse existing subscription if present and matches current key, otherwise re-subscribe
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await sub.unsubscribe(); } catch {}
    }
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });

    // POST to backend
    const subJson = sub.toJSON();
    await api.post('/push/subscribe', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    });
    return { ok: true };
  } catch (err) {
    console.warn('[WEB-PUSH] subscribe failed:', err.message || err);
    return { ok: false, reason: 'error', error: err };
  }
}

export async function unsubscribeWebPush() {
  if (!isWebPushSupported()) return { ok: false, reason: 'unsupported' };
  try {
    const reg = await getRegistration();
    if (!reg) return { ok: false, reason: 'no-sw' };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await api.post('/push/unsubscribe', { endpoint }).catch(() => {});
    return { ok: true };
  } catch (err) {
    console.warn('[WEB-PUSH] unsubscribe failed:', err.message || err);
    return { ok: false, error: err };
  }
}

/**
 * Has THIS browser already subscribed for this user? Quick local check —
 * doesn't hit the backend.
 */
export async function isWebPushSubscribed() {
  if (!isWebPushSupported()) return false;
  try {
    const reg = await getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}
